import { useAuth } from "@contexts/auth";
import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  Group,
  Modal,
  Radio,
  ScrollAreaAutosize,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconPencil } from "@tabler/icons-react";
import i18n from "src/i18n";
import { z } from "zod";

import { showErrorNotification } from "@utils/showNotification";

import { imageFileTypes } from "@constants/fileTypes";

import { useCreateProfile } from "@hooks/mutations/profile/useCreateProfile";
import { useGetProfiles } from "@hooks/queries/profile/useGetProfiles";
import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";

import { StyledAddDoctorModalContent } from "../AddDoctorModal/AddDoctorModal.styled";


const laboratoryFormSchema = () =>
  z.object({
    laboratory: z.string().min(1, i18n.t("laboratory-required")),
    location: z.string().optional(),
    yearsOfService: z.string().optional(),
    bio: z.string().optional(),
    visibility: z.enum(["public", "private"]),
  });

type LaboratoryFormInputs = z.infer<ReturnType<typeof laboratoryFormSchema>>;

interface AddLaboratoryModalProps {
  opened: boolean;
  close: () => void;
}

// File upload state interface (separate from form data)
interface FileUploads {
  photo?: File;
}

export const AddLaboratoryModal = ({ opened, close }: AddLaboratoryModalProps) => {
  const { t } = useTranslation();
  const { can } = useAuth();

  const [isSaving, setIsSaving] = useState(false);

  // File uploads state (separate from form data)
  const [fileUploads, setFileUploads] = useState<FileUploads>({
    photo: undefined,
  });

  // Mantine form with zodResolver
  const form = useForm<LaboratoryFormInputs>({
    mode: "uncontrolled",
    validate: zodResolver(laboratoryFormSchema()),
    initialValues: {
      laboratory: "",
      location: "",
      yearsOfService: "",
      bio: "",
      visibility: can("profiles.publish") ? "public" : "private",
    },
  });

  // Fetch all partners (laboratories)
  const { data: partnersData } = useGetPartners({ isEnabled: opened, isActive: true });

  // Fetch existing laboratory profiles to filter out partners who already have profiles
  const { data: existingProfilesData, isLoading: isLoadingProfiles } = useGetProfiles({
    type: "lab",
    visibility: "all",
    isEnabled: opened,
  });

  // Filter out partners who already have profiles
  const availablePartners = useMemo(() => {
    if (!partnersData || !existingProfilesData?.newData) return partnersData || [];

    const existingPartnerIds = existingProfilesData.newData
      .filter(profile => profile.partnerId)
      .map(profile => profile.partnerId);

    return partnersData.filter(partner => !existingPartnerIds.includes(partner._id));
  }, [partnersData, existingProfilesData]);

  // Profile creation mutation
  const { mutate: createProfile, isLoading: isCreatingProfile } = useCreateProfile();

  // Photo upload handling
  const onPhotoDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFileUploads(prev => ({ ...prev, photo: acceptedFiles[0] }));
    }
  }, []);

  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps } = useDropzone({
    onDrop: onPhotoDrop,
    accept: { "image/*": imageFileTypes },
    multiple: false,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const handleSave = (formValues: LaboratoryFormInputs) => {
    if (!formValues.laboratory) {
      showErrorNotification({
        title: "validation-error",
        message: "laboratory-required",
      });
      return;
    }

    setIsSaving(true);

    // Find selected partner
    const selectedPartner = availablePartners?.find(partner => partner._id === formValues.laboratory);
    if (!selectedPartner) {
      showErrorNotification({
        title: "error",
        message: "selected-laboratory-not-found",
      });
      setIsSaving(false);
      return;
    }

    // Create FormData for API submission
    const submitData = new FormData();

    // Basic profile data
    submitData.append("profile_type", "lab");
    submitData.append("partner_id", selectedPartner._id);
    submitData.append("location", formValues.location || "");
    submitData.append("years_of_experience", formValues.yearsOfService || "");
    submitData.append("brief_description", formValues.bio || "");
    submitData.append("visibility", formValues.visibility);

    // Add photo if provided
    if (fileUploads.photo) {
      submitData.append("profile_image", fileUploads.photo);
    }

    createProfile(submitData, {
      onSuccess: () => {
        setIsSaving(false);
        close();
      },
      onError: (error) => {
        console.error("Profile creation failed:", error);
        setIsSaving(false);
      },
    });
  };

  const handleClose = () => {
    form.reset();
    setFileUploads({
      photo: undefined,
    });
    close();
  };

  return (
    <Modal
      centered
      size="md"
      title={<Text fw={500}>{t("add-profile")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
    >
      <StyledAddDoctorModalContent>
        <form
          onSubmit={form.onSubmit(
            (values) => handleSave(values),
            (errors) => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}
        >
          <Text className="step-title">
            {t("laboratory-info")}
          </Text>

          <Stack gap="md">
            {/* Partner's Logo */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                {t("partners-logo")}
              </Text>
              <Group justify="center" mb="md">
                <div style={{ position: "relative" }}>
                  <Avatar size={80} radius="99px" src={fileUploads.photo ? URL.createObjectURL(fileUploads.photo) : null} />
                  <div
                    {...getPhotoRootProps()}
                    className="photo-upload-button"
                  >
                    <input {...getPhotoInputProps()} />
                    <IconPencil size={14} color="white" />
                  </div>
                </div>
              </Group>
            </div>

            {/* Laboratory Selection */}
            <Select
              label={t("laboratory-actual")}
              placeholder={t("select-laboratory")}
              data={availablePartners?.map(partner => ({
                value: partner._id,
                label: partner.name,
              })) || []}
              {...form.getInputProps("laboratory")}
              key={form.key("laboratory")}
              searchable
              disabled={isLoadingProfiles}
            />

            {/* Location */}
            <TextInput
              label={t("location")}
              placeholder={t("enter-location")}
              {...form.getInputProps("location")}
              key={form.key("location")}
            />

            {/* Years of Service */}
            <TextInput
              label={t("years-of-service")}
              placeholder={t("enter-years-of-service")}
              {...form.getInputProps("yearsOfService")}
              key={form.key("yearsOfService")}
            />

            {/* Bio */}
            <Textarea
              label={t("bio")}
              placeholder={t("enter-bio")}
              rows={4}
              {...form.getInputProps("bio")}
              key={form.key("bio")}
            />

            {/* Visibility */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                {t("visibility")}
              </Text>
              <Radio.Group
                {...form.getInputProps("visibility")}
                key={form.key("visibility")}
              >
                <Stack gap={"0.1rem"}>
                  <Radio value="public" disabled={!can("profiles.publish")} label={t("public")} />
                  <Radio value="private" disabled={!can("profiles.publish")} label={t("private")} />
                </Stack>
              </Radio.Group>
            </div>

            {/* Actions */}
            <Group justify="end" mt="xl">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                {t("cancel")}
              </Button>
              <PermissionGate permission="profiles.create"><Button
                type="submit"
                loading={isSaving || isCreatingProfile}
                disabled={!form.getValues().laboratory}
              >
                {t("save")}
              </Button></PermissionGate>
            </Group>
          </Stack>
        </form>
      </StyledAddDoctorModalContent>
    </Modal>
  );
};
